import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { getSocket } from '../../hooks/useSocket';
import toast from 'react-hot-toast';
import './Admin.css';

const DIAS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];
const DIAS_LABEL = { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sáb', dom: 'Dom' };

const horarioPadrao = {
  seg: { ativo: true, inicio: '08:00', fim: '18:00' },
  ter: { ativo: true, inicio: '08:00', fim: '18:00' },
  qua: { ativo: true, inicio: '08:00', fim: '18:00' },
  qui: { ativo: true, inicio: '08:00', fim: '18:00' },
  sex: { ativo: true, inicio: '08:00', fim: '18:00' },
  sab: { ativo: false, inicio: '08:00', fim: '12:00' },
  dom: { ativo: false, inicio: '08:00', fim: '12:00' },
};

export default function AdminCanais() {
  const [canais, setCanais] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(null);
  const [canalSelecionado, setCanalSelecionado] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrStatus, setQrStatus] = useState('aguardando'); // aguardando | recebido | conectado
  const [form, setForm] = useState({});
  const [atendentesVinculados, setAtendentesVinculados] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    const [c, u] = await Promise.all([api.get('/canais'), api.get('/usuarios')]);
    setCanais(c.data);
    setUsuarios(u.data);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Escuta eventos de QR e conexão via Socket.IO em tempo real
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onQr = ({ canal_id, qr }) => {
      if (canal_id !== canalSelecionado?.id) return;
      console.log('QR recebido via socket');
      setQrCode(qr);
      setQrStatus('recebido');
    };

    const onConectado = ({ canal_id }) => {
      if (canal_id !== canalSelecionado?.id) return;
      setQrStatus('conectado');
      toast.success('WhatsApp conectado!');
      setModal(null);
      carregar();
    };

    const onDesconectado = ({ canal_id }) => {
      carregar();
    };

    socket.on('canal:qr', onQr);
    socket.on('canal:conectado', onConectado);
    socket.on('canal:desconectado', onDesconectado);

    return () => {
      socket.off('canal:qr', onQr);
      socket.off('canal:conectado', onConectado);
      socket.off('canal:desconectado', onDesconectado);
    };
  }, [canalSelecionado, carregar]);

  const abrirNovo = () => {
    setForm({
      nome: '', numero: '', descricao: '',
      mensagem_boas_vindas: 'Olá! Bem-vindo ao atendimento da Lojas Presidente. Em breve um atendente irá lhe ajudar. Sua posição na fila: {posicao}',
      mensagem_encerramento: 'Seu atendimento foi encerrado. Obrigado por entrar em contato!',
      mensagem_fora_horario: 'Nosso horário de atendimento é das {hora_inicio} às {hora_fim}. Retornaremos em breve!',
      timeout_inatividade_minutos: 30,
      mensagem_aviso_inatividade: 'Seu atendimento será encerrado em {minutos} minutos por inatividade.',
      horarios_atendimento: horarioPadrao,
      menu_atendimento: [],
    });
    setModal('novo');
  };

  const abrirEditar = (canal) => {
    setCanalSelecionado(canal);
    const vinculados = usuarios.filter(u => u.canais?.some(c => c.id === canal.id)).map(u => u.id);
    setAtendentesVinculados(vinculados);
    setForm({
      nome: canal.nome,
      numero: canal.numero,
      descricao: canal.descricao || '',
      mensagem_boas_vindas: canal.mensagem_boas_vindas,
      mensagem_encerramento: canal.mensagem_encerramento,
      mensagem_fora_horario: canal.mensagem_fora_horario,
      timeout_inatividade_minutos: canal.timeout_inatividade_minutos,
      mensagem_aviso_inatividade: canal.mensagem_aviso_inatividade || 'Seu atendimento será encerrado em {minutos} minutos por inatividade.',
      horarios_atendimento: canal.horarios_atendimento || horarioPadrao,
      menu_atendimento: canal.menu_atendimento || [],
      ativo: canal.ativo,
    });
    setModal('editar');
  };

  const abrirAtendentes = (canal) => {
    setCanalSelecionado(canal);
    const vinculados = usuarios.filter(u => u.canais?.some(c => c.id === canal.id)).map(u => u.id);
    setAtendentesVinculados(vinculados);
    setModal('atendentes');
  };

  const excluirCanal = async (canal) => {
    if (!confirm(`Excluir o canal "${canal.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/canais/${canal.id}`);
      toast.success('Canal excluído');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao excluir canal');
    }
  };

  // Polling fallback para QR Code
  useEffect(() => {
    if (modal !== 'qr' || qrStatus !== 'aguardando' || !canalSelecionado?.id) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/canais/${canalSelecionado.id}`);
        if (data.qr_code && data.status_conexao === 'aguardando_qr') {
          setQrCode(data.qr_code);
          setQrStatus('recebido');
        } else if (data.status_conexao === 'conectado') {
          setQrStatus('conectado');
          clearInterval(interval);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [modal, qrStatus, canalSelecionado?.id]);

  const abrirQR = async (canal) => {
    setCanalSelecionado(canal);
    setQrCode(null);
    setQrStatus('aguardando');
    setModal('qr');
    try {
      await api.post(`/canais/${canal.id}/conectar`);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao iniciar conexão');
      setModal(null);
    }
  };

  const desconectar = async (canal) => {
    if (!confirm(`Desconectar o canal "${canal.nome}"?`)) return;
    try {
      await api.post(`/canais/${canal.id}/desconectar`);
      toast.success('Desconectado com sucesso');
      carregar();
    } catch (err) {
      toast.error('Erro ao desconectar');
    }
  };

  const salvarCanal = async () => {
    setSalvando(true);
    try {
      if (modal === 'novo') {
        await api.post('/canais', form);
        toast.success('Canal criado!');
      } else {
        await api.put(`/canais/${canalSelecionado.id}`, form);
        toast.success('Canal atualizado!');
      }
      setModal(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const salvarAtendentes = async () => {
    setSalvando(true);
    try {
      await api.put(`/canais/${canalSelecionado.id}/atendentes`, { usuario_ids: atendentesVinculados });
      toast.success('Atendentes atualizados!');
      setModal(null);
      carregar();
    } catch (err) {
      toast.error('Erro ao salvar atendentes');
    } finally {
      setSalvando(false);
    }
  };

  const toggleAtendente = (id) => {
    setAtendentesVinculados(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const setHorario = (dia, campo, valor) => {
    setForm(f => ({
      ...f,
      horarios_atendimento: {
        ...f.horarios_atendimento,
        [dia]: { ...f.horarios_atendimento[dia], [campo]: valor }
      }
    }));
  };

  const statusLabel = {
    conectado: '🟢 Conectado',
    desconectado: '⚫ Desconectado',
    aguardando_qr: '🟡 Aguardando QR'
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Canais WhatsApp</h1>
          <p>Gerencie os números e configurações de cada canal</p>
        </div>
        <button className="btn-primary" onClick={abrirNovo}>+ Novo Canal</button>
      </div>

      <div className="cards-grid">
        {canais.map(canal => (
          <div key={canal.id} className="canal-card">
            <div className="canal-card-top">
              <div className="canal-card-icon">📱</div>
              <div className="canal-card-info">
                <div className="canal-card-nome">{canal.nome}</div>
                <div className="canal-card-numero">{canal.numero}</div>
              </div>
              <span className={`status-pill ${canal.status_conexao}`}>
                {statusLabel[canal.status_conexao] || canal.status_conexao}
              </span>
            </div>
            <div className="canal-card-acoes">
              <button className="btn-acao-sm" onClick={() => abrirEditar(canal)}>✏️ Configurar</button>
              <button className="btn-acao-sm" onClick={() => abrirAtendentes(canal)}>👥 Atendentes</button>
              {canal.status_conexao === 'conectado'
                ? <button className="btn-acao-sm danger" onClick={() => desconectar(canal)}>🔌 Desconectar</button>
                : <button className="btn-acao-sm success" onClick={() => abrirQR(canal)}>📲 Conectar WhatsApp</button>
              }
              <button className="btn-acao-sm danger" onClick={() => excluirCanal(canal)}>🗑️ Excluir</button>
            </div>
          </div>
        ))}
        {canais.length === 0 && (
          <div className="empty-state">
            <div style={{ fontSize: 40 }}>📱</div>
            <p>Nenhum canal cadastrado ainda.</p>
            <button className="btn-primary" onClick={abrirNovo}>Criar primeiro canal</button>
          </div>
        )}
      </div>

      {/* MODAL NOVO/EDITAR */}
      {(modal === 'novo' || modal === 'editar') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'novo' ? 'Novo Canal' : `Editar — ${canalSelecionado?.nome}`}</h3>
              <button onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-tabs-content">
              <div className="form-section">
                <div className="section-label">Dados do canal</div>
                <div className="form-row">
                  <div className="field">
                    <label>Nome do canal</label>
                    <input value={form.nome || ''} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Vendas" />
                  </div>
                  <div className="field">
                    <label>Número WhatsApp</label>
                    <input value={form.numero || ''} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="5547999990001" />
                  </div>
                </div>
              </div>
              <div className="form-section">
                <div className="section-label">Mensagens automáticas</div>
                <div className="form-group">
                  <label>Boas-vindas</label>
                  <textarea rows={2} value={form.mensagem_boas_vindas || ''} onChange={e => setForm(f => ({ ...f, mensagem_boas_vindas: e.target.value }))} />
                  <span className="hint">Use <strong>{'{posicao}'}</strong> para posição na fila</span>
                </div>
                <div className="form-group">
                  <label>Encerramento</label>
                  <textarea rows={2} value={form.mensagem_encerramento || ''} onChange={e => setForm(f => ({ ...f, mensagem_encerramento: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Fora do horário</label>
                  <textarea rows={2} value={form.mensagem_fora_horario || ''} onChange={e => setForm(f => ({ ...f, mensagem_fora_horario: e.target.value }))} />
                  <span className="hint">Use <strong>{'{hora_inicio}'}</strong> e <strong>{'{hora_fim}'}</strong></span>
                </div>
              </div>
              <div className="form-section">
                <div className="section-label">Encerramento automático por inatividade</div>
                <div className="field inline">
                  <label>Encerrar atendimento após</label>
                  <input type="number" min={0} max={1440} style={{width:80}}
                    value={form.timeout_inatividade_minutos ?? 30}
                    onChange={e => setForm(f => ({ ...f, timeout_inatividade_minutos: parseInt(e.target.value) || 0 }))} />
                  <span className="hint">minutos sem atividade <strong>(0 = desativado)</strong></span>
                </div>
                <div className="field">
                  <label>Mensagem de aviso de inatividade <span className="hint">Use {'{minutos}'} para o tempo restante</span></label>
                  <textarea rows={2} value={form.mensagem_aviso_inatividade || ''}
                    onChange={e => setForm(f => ({ ...f, mensagem_aviso_inatividade: e.target.value }))}
                    placeholder="Deixe vazio para não enviar aviso" />
                </div>
                <div className="field">
                  <label>Encerramento pelo cliente</label>
                  <span className="hint">O cliente pode encerrar digitando <strong>sair</strong> (somente essa palavra).</span>
                </div>
              </div>
              <div className="form-section">
                <div className="section-label">Horários de atendimento</div>
                <div className="horarios-grid">
                  {DIAS.map(dia => {
                    const h = form.horarios_atendimento?.[dia] || { ativo: false, inicio: '08:00', fim: '18:00' };
                    return (
                      <div key={dia} className={`horario-row ${h.ativo ? 'ativo' : ''}`}>
                        <label className="toggle">
                          <input type="checkbox" checked={h.ativo} onChange={e => setHorario(dia, 'ativo', e.target.checked)} />
                          <span>{DIAS_LABEL[dia]}</span>
                        </label>
                        {h.ativo ? (
                          <>
                            <input type="time" value={h.inicio} onChange={e => setHorario(dia, 'inicio', e.target.value)} />
                            <span>às</span>
                            <input type="time" value={h.fim} onChange={e => setHorario(dia, 'fim', e.target.value)} />
                          </>
                        ) : <span className="fechado">Fechado</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="form-section">
              <div className="section-label">Menu de Atendimento</div>
              <span className="hint" style={{marginBottom:12,display:'block'}}>O cliente verá este menu ao entrar em contato. Deixe vazio para não usar menu.</span>
              {(form.menu_atendimento || []).map((op, i) => (
                <div key={i} className="horario-row ativo" style={{flexDirection:'column',alignItems:'stretch',marginBottom:6}}>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{minWidth:24,fontWeight:600,fontSize:13}}>{i+1}.</span>
                    <input
                      style={{flex:1}}
                      className="field"
                      value={op.texto}
                      onChange={e => {
                        const novas = [...(form.menu_atendimento || [])];
                        novas[i] = { ...novas[i], texto: e.target.value };
                        setForm(f => ({ ...f, menu_atendimento: novas }));
                      }}
                      placeholder={`Texto da opção ${i+1}`}
                    />
                    <button className="btn-secondary" style={{padding:'4px 10px',flexShrink:0}} onClick={() => {
                      const novas = (form.menu_atendimento || []).filter((_, j) => j !== i);
                      setForm(f => ({ ...f, menu_atendimento: novas }));
                    }}>✕</button>
                  </div>
                  <div className="form-group" style={{marginTop:8,marginBottom:0,paddingLeft:8}}>
                    <label>Atendentes exclusivos <span style={{fontWeight:400,opacity:0.6}}>(deixe vazio para qualquer atendente)</span></label>
                    <div style={{display:'flex',flexDirection:'column',gap:2,marginTop:4}}>
                      {usuarios.filter(u => u.perfil === 'atendente' && atendentesVinculados.includes(u.id)).map(a => {
                        const ids = op.atendentes_ids || (op.atendente_id ? [op.atendente_id] : []);
                        return (
                          <label key={a.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',padding:'3px 0'}}>
                            <input type="checkbox"
                              style={{flexShrink:0}}
                              checked={ids.includes(a.id)}
                              onChange={e => {
                                const novas = [...(form.menu_atendimento || [])];
                                const idsAtuais = novas[i].atendentes_ids || (novas[i].atendente_id ? [novas[i].atendente_id] : []);
                                const novosIds = e.target.checked
                                  ? [...idsAtuais, a.id]
                                  : idsAtuais.filter(id => id !== a.id);
                                novas[i] = { ...novas[i], atendentes_ids: novosIds, atendente_id: null };
                                setForm(f => ({ ...f, menu_atendimento: novas }));
                              }}
                            />
                            <span style={{lineHeight:1.3}}>{a.nome}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn-secondary" style={{marginTop:8}} onClick={() => {
                setForm(f => ({ ...f, menu_atendimento: [...(f.menu_atendimento || []), { texto: '' }] }));
              }}>+ Adicionar opção</button>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarCanal} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ATENDENTES */}
      {modal === 'atendentes' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Atendentes — {canalSelecionado?.nome}</h3>
              <button onClick={() => setModal(null)}>✕</button>
            </div>
            <p className="modal-sub">Marque quem pode atender neste canal:</p>
            <div className="atendentes-lista">
              {usuarios.filter(u => u.perfil === 'atendente').map(u => (
                <label key={u.id} className="atendente-check">
                  <input type="checkbox"
                    checked={atendentesVinculados.includes(u.id)}
                    onChange={() => toggleAtendente(u.id)} />
                  <div className="at-avatar">{u.nome.slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div className="at-nome">{u.nome}</div>
                    <div className="at-user">@{u.usuario}</div>
                  </div>
                </label>
              ))}
              {usuarios.filter(u => u.perfil === 'atendente').length === 0 && (
                <p style={{ color: 'var(--texto-sec)', fontSize: 13 }}>Nenhum atendente cadastrado.</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarAtendentes} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL QR CODE */}
      {modal === 'qr' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <div className="modal-header">
              <h3>Conectar WhatsApp — {canalSelecionado?.nome}</h3>
              <button onClick={() => setModal(null)}>✕</button>
            </div>

            {qrStatus === 'conectado' && (
              <div style={{ padding: '32px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <p style={{ fontWeight: 500, color: 'var(--verde)' }}>WhatsApp conectado com sucesso!</p>
              </div>
            )}

            {qrStatus === 'aguardando' && (
              <div style={{ padding: '40px 0', color: 'var(--texto-sec)' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                <p>Gerando QR Code, aguarde...</p>
                <p style={{ fontSize: 12, marginTop: 8, color: 'var(--texto-ter)' }}>
                  Isso pode levar alguns segundos
                </p>

              </div>
            )}

            {qrStatus === 'recebido' && qrCode && (
              <>
                <p className="modal-sub" style={{ padding: '12px 20px' }}>
                  Abra o WhatsApp → Dispositivos conectados → Conectar dispositivo → Escaneie:
                </p>
                <img src={qrCode} alt="QR Code WhatsApp"
                  style={{ width: 240, height: 240, margin: '8px auto 12px', display: 'block', borderRadius: 8, border: '1px solid var(--cinza-borda)' }} />
                <p style={{ fontSize: 12, color: 'var(--texto-sec)', marginBottom: 16 }}>
                  O QR Code expira em 60 segundos — um novo será gerado automaticamente.
                </p>
              </>
            )}

            <div style={{ padding: '0 20px 20px', display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => setModal(null)}>Fechar</button>
              {qrStatus !== 'conectado' && (
                <button className="btn-primary" onClick={() => abrirQR(canalSelecionado)}>
                  🔄 Gerar novo QR
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
